/*
 * PDP multi-moulding calculator (vanilla port of PdpCalculator.jsx).
 *
 * Four modes, each with its own real-world method:
 *   Skirting    — room perimeter minus door openings
 *   Architrave  — per-door-face legs + head, bin-packed into boards
 *   Rail        — perimeter at height, interrupted by door + architrave
 *   Panel       — Shaker grid / Board & batten / Panel moulding cut lists
 *
 * Init: scans for [data-pdp-calc] roots and wires each one up. Idempotent.
 */
(function () {
  if (window.__pdpCalcInit) return;
  window.__pdpCalcInit = true;

  var WASTE = 1.10;
  var DEFAULT_LENGTHS = ['2.2 m', '3.0 m', '4.4 m'];

  function fmt(n, d) {
    if (d == null) d = 2;
    var num = Number(n);
    if (!isFinite(num)) num = 0;
    return num.toFixed(d);
  }

  // Board lengths as the shop writes them: "2.2 m", "3.0 m", "4.4 m" (never "2.20 m").
  function fmtLen(n) {
    var num = Number(n);
    if (!isFinite(num)) return '0 m';
    var out = (Math.round(num * 100) / 100).toString();
    if (out.indexOf('.') === -1) out += '.0';
    return out + ' m';
  }

  // "2.2m", "2.2 m", "2200mm", "4.4 m Primed" → metres (number) or null.
  function parseMetres(text) {
    var m = String(text || '').replace(/,/g, '.').match(/(\d+(?:\.\d+)?)\s*(mm|m)\b/i);
    if (!m) return null;
    var v = parseFloat(m[1]);
    return m[2].toLowerCase() === 'mm' ? v / 1000 : v;
  }

  // First-fit-decreasing bin-packing — architrave pieces can't be spliced from
  // offcuts, so we pack discrete pieces (mm) into boards and count the boards.
  function packBoards(pieces, boardMm) {
    var sorted = pieces.filter(function (p) { return p > 0; }).slice().sort(function (a, b) { return b - a; });
    var remaining = [];
    sorted.forEach(function (p) {
      if (p > boardMm) { remaining.push(0); return; } // oversize → own board
      var placed = false;
      for (var i = 0; i < remaining.length; i++) {
        if (remaining[i] >= p) { remaining[i] -= p; placed = true; break; }
      }
      if (!placed) remaining.push(boardMm - p);
    });
    return remaining.length;
  }

  // Tiny DOM-element helper. Children may be strings, numbers, nodes, arrays, or null/false (skipped).
  function el(tag, attrs) {
    var node = tag.indexOf(':svg-') === 0
      ? document.createElementNS('http://www.w3.org/2000/svg', tag.replace(':svg-', ''))
      : document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        var v = attrs[k];
        if (v == null || v === false) return;
        if (k === 'className') node.setAttribute('class', v);
        else if (k === 'html') node.innerHTML = v;
        else if (k.indexOf('on') === 0 && typeof v === 'function') node.addEventListener(k.slice(2).toLowerCase(), v);
        else node.setAttribute(k, v);
      });
    }
    for (var i = 2; i < arguments.length; i++) {
      var child = arguments[i];
      if (Array.isArray(child)) child.forEach(function (c) { append(node, c); });
      else append(node, child);
    }
    return node;
  }
  function append(parent, child) {
    if (child == null || child === false) return;
    if (typeof child === 'string' || typeof child === 'number') {
      parent.appendChild(document.createTextNode(String(child)));
    } else {
      parent.appendChild(child);
    }
  }

  function row(label, value, sub) {
    return el('div', { className: 'pdp-calc__row' },
      el('span', { className: 'pdp-calc__row-label' },
        label,
        sub ? el('small', { className: 'pdp-calc__row-sub' }, sub) : null
      ),
      el('span', { className: 'pdp-calc__row-value' }, value)
    );
  }

  function totalRow(value) {
    return el('div', { className: 'pdp-calc__row pdp-calc__row--total' },
      el('span', { className: 'pdp-calc__row-label' }, 'You need'),
      el('span', { className: 'pdp-calc__row-value' }, value)
    );
  }

  function field(label, value, onInput, step) {
    var input = el('input', {
      type: 'number',
      step: step || '0.1',
      value: value,
      class: 'pdp-calc__input',
      onInput: function (e) { onInput(e.target.value); }
    });
    return el('label', { className: 'pdp-calc__field' },
      el('span', { className: 'pdp-calc__field-label' }, label),
      input
    );
  }

  function pill(text, active, onClick, small) {
    return el('button', {
      type: 'button',
      class: 'pdp-calc__pill' + (active ? ' is-active' : '') + (small ? ' pdp-calc__pill--sm' : ''),
      onClick: onClick
    }, text);
  }

  // ============================================================================
  //  SKIRTING
  // ============================================================================
  var Skirting = {
    label: 'Skirting',
    init: function () { return { length: 4.2, width: 3.6, doors: 1, doorWidth: 0.838 }; },
    note: 'Skirting runs continuously around the floor. We take the room perimeter, subtract door openings, then add a 10% buffer for mitres and offcuts.',
    compute: function (s, board) {
      var perimeter = 2 * (s.length + s.width);
      var deduction = s.doors * s.doorWidth;
      var linear = Math.max(0, perimeter - deduction);
      var total = linear * WASTE;
      var lengths = Math.ceil(total / board);
      return { perimeter: perimeter, deduction: deduction, linear: linear, total: total, lengths: lengths };
    },
    body: function (s, set) {
      return [
        el('p', { className: 'pdp-calc__note' }, this.note),
        el('div', { className: 'pdp-calc__inputs' },
          field('Room length (m)', s.length, function (v) { set('length', v); }, '0.1'),
          field('Room width (m)', s.width, function (v) { set('width', v); }, '0.1'),
          field('Number of doors', s.doors, function (v) { set('doors', v); }, '1'),
          field('Door width (m)', s.doorWidth, function (v) { set('doorWidth', v); }, '0.01')
        )
      ];
    },
    result: function (s, board, calc) {
      return [
        el('h3', { className: 'pdp-calc__result-heading' }, 'For your ' + s.length + ' × ' + s.width + ' m room'),
        row('Room perimeter', fmt(calc.perimeter) + ' m'),
        row('Less ' + s.doors + ' door opening' + (s.doors === 1 ? '' : 's'), '−' + fmt(calc.deduction) + ' m'),
        row('Skirting required', fmt(calc.total) + ' m', '+10% wastage applied'),
        row('Per ' + fmtLen(board) + ' length', calc.lengths + ' lengths'),
        totalRow(calc.lengths + ' × ' + fmtLen(board))
      ];
    }
  };

  // ============================================================================
  //  ARCHITRAVE
  // ============================================================================
  var Architrave = {
    label: 'Architrave',
    init: function () { return { doors: 3, faces: 2, doorH: 1981, doorW: 762, archW: 70 }; },
    note: 'Architrave frames each opening on three sides — two legs and a head, mitred at 45°. A "set" covers one face of a door; both faces need two sets. We size each piece, then pack them into boards (legs can\'t be joined from offcuts).',
    LINING: 32, // mm — standard internal door-lining thickness
    compute: function (s, board) {
      var boardMm = board * 1000;
      var legLen = s.doorH + s.archW + 10;            // + mitre allowance
      var headLen = s.doorW + 2 * this.LINING + 2 * s.archW;
      var perFace = 2 * legLen + headLen;
      var totalFaces = Math.round(s.doors * s.faces);
      var pieces = [];
      for (var i = 0; i < totalFaces; i++) { pieces.push(legLen, legLen, headLen); }
      var boardsNeeded = packBoards(pieces, boardMm);
      var totalLm = (perFace * totalFaces) / 1000;
      return { legLen: legLen, headLen: headLen, totalFaces: totalFaces, totalLm: totalLm, boardsNeeded: boardsNeeded, lengths: boardsNeeded };
    },
    body: function (s, set) {
      return [
        el('p', { className: 'pdp-calc__note' }, this.note),
        el('div', { className: 'pdp-calc__sub' },
          pill('One face', s.faces === 1, function () { set('faces', 1); }),
          pill('Both faces', s.faces === 2, function () { set('faces', 2); })
        ),
        el('div', { className: 'pdp-calc__inputs' },
          field('Number of doors', s.doors, function (v) { set('doors', v); }, '1'),
          field('Architrave width (mm)', s.archW, function (v) { set('archW', v); }, '1'),
          field('Door height (mm)', s.doorH, function (v) { set('doorH', v); }, '1'),
          field('Door width (mm)', s.doorW, function (v) { set('doorW', v); }, '1')
        )
      ];
    },
    result: function (s, board, calc) {
      return [
        el('h3', { className: 'pdp-calc__result-heading' }, s.doors + ' door' + (s.doors === 1 ? '' : 's') + ' · ' + calc.totalFaces + ' face' + (calc.totalFaces === 1 ? '' : 's')),
        row('2 legs per face', fmt(calc.legLen / 1000) + ' m ea'),
        row('1 head per face', fmt(calc.headLen / 1000) + ' m'),
        row('Total architrave', fmt(calc.totalLm) + ' m', 'incl. mitre allowance'),
        row('Packed into ' + fmtLen(board) + ' lengths', calc.boardsNeeded + ' lengths'),
        totalRow(calc.boardsNeeded + ' × ' + fmtLen(board)),
        el('p', { className: 'pdp-calc__foot' },
          'Equivalent to ',
          el('strong', null, calc.totalFaces + ' architrave set' + (calc.totalFaces === 1 ? '' : 's')),
          ' · one set per door face.'
        )
      ];
    }
  };

  // ============================================================================
  //  DADO / PICTURE RAIL
  // ============================================================================
  var Rail = {
    label: 'Dado / Picture Rail',
    init: function () { return { railType: 'dado', length: 4.2, width: 3.6, doors: 1, archW: 70 }; },
    notes: {
      dado: 'Dado (chair) rail runs horizontally at roughly 900 mm from the floor. It runs around the room and stops dead against each door architrave.',
      picture: 'Picture rail sits higher — typically 300–500 mm below the ceiling. It rings the room uninterrupted except where doorways break the run.'
    },
    compute: function (s, board) {
      var openWidth = 0.838 + (2 * s.archW) / 1000;
      var perimeter = 2 * (s.length + s.width);
      var deduction = s.doors * openWidth;
      var linear = Math.max(0, perimeter - deduction);
      var total = linear * WASTE;
      var lengths = Math.ceil(total / board);
      return { perimeter: perimeter, deduction: deduction, linear: linear, total: total, lengths: lengths };
    },
    body: function (s, set) {
      var note = this.notes[s.railType] + ' Each doorway removes its opening plus the architrave on both sides.';
      return [
        el('div', { className: 'pdp-calc__sub' },
          pill('Dado rail', s.railType === 'dado', function () { set('railType', 'dado'); }),
          pill('Picture rail', s.railType === 'picture', function () { set('railType', 'picture'); })
        ),
        el('p', { className: 'pdp-calc__note' }, note),
        el('div', { className: 'pdp-calc__inputs' },
          field('Room length (m)', s.length, function (v) { set('length', v); }, '0.1'),
          field('Room width (m)', s.width, function (v) { set('width', v); }, '0.1'),
          field('Door openings', s.doors, function (v) { set('doors', v); }, '1'),
          field('Architrave width (mm)', s.archW, function (v) { set('archW', v); }, '1')
        )
      ];
    },
    result: function (s, board, calc) {
      var name = s.railType === 'dado' ? 'Dado rail' : 'Picture rail';
      return [
        el('h3', { className: 'pdp-calc__result-heading' }, name + ' · ' + s.length + ' × ' + s.width + ' m'),
        row('Room perimeter', fmt(calc.perimeter) + ' m'),
        row('Less ' + s.doors + ' doorway' + (s.doors === 1 ? '' : 's'), '−' + fmt(calc.deduction) + ' m', 'opening + architrave'),
        row('Rail required', fmt(calc.total) + ' m', '+10% wastage applied'),
        row('Per ' + fmtLen(board) + ' length', calc.lengths + ' lengths'),
        totalRow(calc.lengths + ' × ' + fmtLen(board)),
        el('p', { className: 'pdp-calc__foot' }, 'Tip: fit the rail level around the room, not parallel to a sloping floor or ceiling.')
      ];
    }
  };

  // ============================================================================
  //  WALL PANELLING
  // ============================================================================
  function svgEl(tag, attrs) {
    var node = document.createElementNS('http://www.w3.org/2000/svg', tag);
    if (attrs) Object.keys(attrs).forEach(function (k) { node.setAttribute(k, attrs[k]); });
    return node;
  }

  function panelDiagram(style, cols, rows, battens) {
    var W = 200, H = 130, pad = 8;
    var svg = svgEl('svg', { viewBox: '0 0 ' + W + ' ' + H, width: '100%', class: 'pdp-calc__diagram' });
    if (style !== 'moulding') {
      svg.appendChild(svgEl('rect', {
        x: pad, y: pad, width: W - 2 * pad, height: H - 2 * pad,
        fill: 'none', stroke: 'var(--mr-slate)', 'stroke-width': '3'
      }));
    }
    if (style === 'shaker') {
      for (var c = 1; c < cols; c++) {
        var x = pad + (c * (W - 2 * pad)) / cols;
        svg.appendChild(svgEl('line', { x1: x, y1: pad, x2: x, y2: H - pad, stroke: 'var(--mr-slate)', 'stroke-width': '3' }));
      }
      for (var rr = 1; rr < rows; rr++) {
        var y = pad + (rr * (H - 2 * pad)) / rows;
        svg.appendChild(svgEl('line', { x1: pad, y1: y, x2: W - pad, y2: y, stroke: 'var(--mr-slate)', 'stroke-width': '3' }));
      }
    } else if (style === 'batten') {
      for (var b = 1; b < battens - 1; b++) {
        var bx = pad + (b * (W - 2 * pad)) / (battens - 1);
        svg.appendChild(svgEl('line', { x1: bx, y1: pad, x2: bx, y2: H - pad, stroke: 'var(--mr-slate)', 'stroke-width': '3' }));
      }
    } else {
      var inset = 14;
      var cw = (W - 2 * pad) / cols, rh = (H - 2 * pad) / rows;
      for (var mc = 0; mc < cols; mc++) {
        for (var mr = 0; mr < rows; mr++) {
          svg.appendChild(svgEl('rect', {
            x: pad + mc * cw + inset / 2, y: pad + mr * rh + inset / 2,
            width: cw - inset, height: rh - inset,
            fill: 'none', stroke: 'var(--mr-slate)', 'stroke-width': '2.5'
          }));
        }
      }
    }
    return svg;
  }

  var Panel = {
    label: 'Wall Panelling',
    init: function () {
      return {
        style: 'shaker',
        wallW: 4.0, wallH: 2.4, battenW: 70,
        cols: 4, rows: 1, battens: 6, gap: 120, mouldW: 30
      };
    },
    notes: {
      shaker: 'Shaker panelling is a grid of full-width rails and full-height stiles framing flat panels. We total the horizontal rails and vertical stiles separately.',
      batten: 'Board & batten is a single row of evenly-spaced vertical battens between a top and bottom rail. Adjust the batten count to balance the spacing.',
      moulding: 'Panel (picture-frame) moulding applies decorative rectangles to a flat wall. We size each frame from your wall, then add a mitre allowance on all four corners.'
    },
    compute: function (s, board) {
      var bW = s.battenW / 1000;
      var rows, total;
      if (s.style === 'shaker') {
        var horiz = (s.rows + 1) * s.wallW;
        var stileH = Math.max(0, s.wallH - (s.rows + 1) * bW);
        var vert = (s.cols + 1) * stileH;
        total = horiz + vert;
        var panelW = (s.wallW - (s.cols + 1) * bW) / s.cols;
        var panelH = (s.wallH - (s.rows + 1) * bW) / s.rows;
        rows = [
          { l: (s.rows + 1) + ' horizontal rails', v: fmt(horiz) + ' m', s: '× ' + fmt(s.wallW) + ' m wide' },
          { l: (s.cols + 1) + ' vertical stiles', v: fmt(vert) + ' m', s: '× ' + fmt(stileH) + ' m tall' },
          { l: 'Each panel opening', v: fmt(Math.max(0, panelW), 2) + ' × ' + fmt(Math.max(0, panelH), 2) + ' m', s: (s.cols * s.rows) + ' panels' }
        ];
      } else if (s.style === 'batten') {
        var horizB = 2 * s.wallW;
        var stileHB = Math.max(0, s.wallH - 2 * bW);
        var vertB = s.battens * stileHB;
        total = horizB + vertB;
        var gap = s.battens > 1 ? (s.wallW - s.battens * bW) / (s.battens - 1) : 0;
        rows = [
          { l: '2 horizontal rails', v: fmt(horizB) + ' m', s: 'top + bottom' },
          { l: s.battens + ' vertical battens', v: fmt(vertB) + ' m', s: '× ' + fmt(stileHB) + ' m tall' },
          { l: 'Gap between battens', v: fmt(Math.max(0, gap), 2) + ' m', s: 'evenly spaced' }
        ];
      } else {
        var g = s.gap / 1000;
        var pW = Math.max(0, (s.wallW - (s.cols + 1) * g) / s.cols);
        var pH = Math.max(0, (s.wallH - (s.rows + 1) * g) / s.rows);
        var panels = s.cols * s.rows;
        var perPanel = 2 * (pW + pH) + 4 * (s.mouldW / 1000);
        total = panels * perPanel;
        rows = [
          { l: panels + ' picture-frame panels', v: fmt(pW, 2) + ' × ' + fmt(pH, 2) + ' m', s: 'even margins' },
          { l: 'Moulding per panel', v: fmt(perPanel) + ' m', s: 'incl. 4 mitres' },
          { l: s.gap + ' mm margins/gaps', v: s.cols + '×' + s.rows + ' layout', s: '' }
        ];
      }
      var lengths = Math.ceil((total * WASTE) / board);
      return { rows: rows, total: total, lengths: lengths };
    },
    body: function (s, set) {
      var STYLES = [
        { v: 'shaker', t: 'Shaker grid' },
        { v: 'batten', t: 'Board & batten' },
        { v: 'moulding', t: 'Panel moulding' }
      ];
      var inputs = [
        field('Wall width (m)', s.wallW, function (v) { set('wallW', v); }, '0.1'),
        field('Panelling height (m)', s.wallH, function (v) { set('wallH', v); }, '0.1')
      ];
      if (s.style === 'moulding') {
        inputs.push(field('Moulding width (mm)', s.mouldW, function (v) { set('mouldW', v); }, '1'));
      } else {
        inputs.push(field('Batten width (mm)', s.battenW, function (v) { set('battenW', v); }, '1'));
      }
      if (s.style === 'batten') {
        inputs.push(field('Number of battens', s.battens, function (v) { set('battens', v); }, '1'));
      } else {
        inputs.push(field('Columns', s.cols, function (v) { set('cols', v); }, '1'));
      }
      if (s.style !== 'batten') {
        inputs.push(field('Rows', s.rows, function (v) { set('rows', v); }, '1'));
      }
      if (s.style === 'moulding') {
        inputs.push(field('Margin / gap (mm)', s.gap, function (v) { set('gap', v); }, '1'));
      }
      return [
        el('div', { className: 'pdp-calc__sub' },
          STYLES.map(function (st) { return pill(st.t, s.style === st.v, function () { set('style', st.v); }); })
        ),
        el('p', { className: 'pdp-calc__note' }, this.notes[s.style]),
        el('div', { className: 'pdp-calc__inputs' }, inputs),
        panelDiagram(s.style, Math.round(s.cols), Math.round(s.rows), Math.round(s.battens))
      ];
    },
    result: function (s, board, calc) {
      var STYLES = { shaker: 'Shaker grid', batten: 'Board & batten', moulding: 'Panel moulding' };
      var rowNodes = calc.rows.map(function (r) { return row(r.l, r.v, r.s); });
      return [
        el('h3', { className: 'pdp-calc__result-heading' }, STYLES[s.style] + ' · ' + fmt(s.wallW) + ' × ' + fmt(s.wallH) + ' m')
      ].concat(rowNodes, [
        row('Total moulding', fmt(calc.total * WASTE) + ' m', '+10% wastage applied'),
        totalRow(calc.lengths + ' × ' + fmtLen(board)),
        el('p', { className: 'pdp-calc__foot' }, 'Supplied as full-length strips to cut on site for an exact fit.')
      ]);
    }
  };

  var MODES = [
    { id: 'skirting', mod: Skirting },
    { id: 'architrave', mod: Architrave },
    { id: 'rail', mod: Rail },
    { id: 'panel', mod: Panel }
  ];

  // ============================================================================
  //  SHELL
  // ============================================================================
  // ---------------------------------------------------------------------------
  //  Product lengths + chooser sync
  //  The calculator's length pills mirror the product's real Length row and stay in
  //  step with it both ways: pick a length here and the buy box selects it; pick it
  //  in the buy box and the calculator follows. Three buy-box flavours:
  //    DPO (Itoris) — <select> inside #itoris_dynamicproductoptions, app-injected late
  //    configurator (mrm.configurable) — [data-mrc-row="Length"] pills
  //    native variants — Empire radios (.options-selection__option-value-input)
  // ---------------------------------------------------------------------------
  function lengthLabelMatches(node) {
    return !!node && /length/i.test(node.textContent || '');
  }

  function findDpoLengthSelect() {
    var selects = document.querySelectorAll('#itoris_dynamicproductoptions select.product-custom-option');
    for (var i = 0; i < selects.length; i++) {
      var field = selects[i].closest('.field');
      if (field && lengthLabelMatches(field.querySelector('label'))) return selects[i];
    }
    return null;
  }

  function findNativeLengthInputs() {
    var inputs = Array.prototype.slice.call(document.querySelectorAll('input.options-selection__option-value-input'));
    return inputs.filter(function (input) {
      var group = input.closest('.options-selection__option') || input.closest('fieldset');
      var legend = group ? group.querySelector('legend, .options-selection__option-name') : null;
      return legend ? lengthLabelMatches(legend) : /length/i.test(input.name || '');
    });
  }

  function chooserLengths() {
    var sel = findDpoLengthSelect();
    if (sel) {
      return {
        kind: 'dpo',
        node: sel,
        options: Array.prototype.filter.call(sel.options, function (o) { return o.value !== '' && parseMetres(o.textContent) !== null; })
          .map(function (o) { return { label: o.textContent.trim(), value: o.value, metres: parseMetres(o.textContent) }; })
      };
    }
    var row = document.querySelector('[data-mrc-row="Length"]');
    if (row) {
      return {
        kind: 'mrc',
        node: row,
        options: Array.prototype.slice.call(row.querySelectorAll('[data-mrc-pill]'))
          .filter(function (p) { return parseMetres(p.dataset.value) !== null; })
          .map(function (p) { return { label: p.dataset.value, value: p.dataset.value, metres: parseMetres(p.dataset.value), pill: p }; })
      };
    }
    var inputs = findNativeLengthInputs();
    if (inputs.length) {
      return {
        kind: 'native',
        node: inputs[0].closest('.options-selection__option') || inputs[0].parentNode,
        options: inputs.filter(function (i) { return parseMetres(i.value) !== null; })
          .map(function (i) { return { label: i.value, value: i.value, metres: parseMetres(i.value), input: i }; })
      };
    }
    return null;
  }

  function chooserSelect(chooser, metres) {
    if (!chooser) return;
    var opt = chooser.options.find(function (o) { return Math.abs(o.metres - metres) < 0.005; });
    if (!opt) return;
    if (chooser.kind === 'dpo') {
      if (chooser.node.value === opt.value) return;
      chooser.node.value = opt.value;
      chooser.node.dispatchEvent(new Event('change', { bubbles: true }));
      if (window.jQuery) { try { window.jQuery(chooser.node).trigger('change'); } catch (e) {} }
    } else if (chooser.kind === 'mrc') {
      if (!opt.pill.classList.contains('is-selected')) opt.pill.click();
    } else if (chooser.kind === 'native') {
      if (!opt.input.checked) { opt.input.checked = true; opt.input.dispatchEvent(new Event('change', { bubbles: true })); }
    }
  }

  function chooserCurrent(chooser) {
    if (!chooser) return null;
    if (chooser.kind === 'dpo') {
      var o = chooser.options.find(function (x) { return x.value === chooser.node.value; });
      return o ? o.metres : null;
    }
    if (chooser.kind === 'mrc') {
      var p = chooser.options.find(function (x) { return x.pill.classList.contains('is-selected'); });
      return p ? p.metres : null;
    }
    var i = chooser.options.find(function (x) { return x.input.checked; });
    return i ? i.metres : null;
  }

  function findQuantityInput() {
    var form = document.querySelector('form[data-product-form], form.product-form, form[action*="/cart/add"]');
    return (form && form.querySelector('[data-quantity-input]')) || document.querySelector('[data-quantity-input]');
  }

  function init(root) {
    if (root.__pdpCalcReady) return;
    root.__pdpCalcReady = true;

    var mode = 'skirting';
    var states = {};
    MODES.forEach(function (m) { states[m.id] = m.mod.init(); });

    var bodyEl = root.querySelector('[data-pdp-calc-body]');
    var resultEl = root.querySelector('[data-pdp-calc-result]');
    var tabsEl = root.querySelector('[data-pdp-calc-tabs]');
    var boardEl = root.querySelector('[data-pdp-calc-board]');

    if (!bodyEl || !resultEl || !tabsEl || !boardEl) return;

    // Scope: the tab matching this product's type ("productMode") uses the product's real
    // lengths and can write the quantity; every other tab is an estimate on typical
    // lengths with a "Shop …" link instead. Empty productMode = generic calculator.
    var productMode = root.getAttribute('data-pdp-calc-mode') || '';
    if (productMode && MODES.some(function (m) { return m.id === productMode; })) mode = productMode;
    var links = {};
    try { links = JSON.parse(root.getAttribute('data-pdp-calc-links') || '{}'); } catch (e) {}
    var LABELS = { skirting: 'skirting', architrave: 'architrave', rail: 'dado and picture rail', panel: 'panel moulding' };
    function isProductTab() { return !!productMode && mode === productMode; }

    // Length pills: server-rendered from the product (or the 2.2/3.0/4.4 default), then
    // rebuilt from the buy box once its Length row exists (DPO injects it late). Product
    // lengths apply to the product tab only; the other tabs keep the typical lengths.
    var productLengths = [];
    var genericLengths = DEFAULT_LENGTHS.map(function (t) { return { label: t, metres: parseMetres(t) }; });
    var lengths = [];
    var boards = {};
    var board = null;
    var chooser = null;
    var confirmText = null;

    function defaultBoard(list) {
      var three = list.find(function (l) { return Math.abs(l.metres - 3) < 0.005; });
      return (three || list[Math.floor((list.length - 1) / 2)] || list[0]).metres;
    }

    function renderPills() {
      var label = boardEl.querySelector('.pdp-calc__board-label');
      boardEl.innerHTML = '';
      if (label) boardEl.appendChild(label);
      lengths.forEach(function (l) {
        boardEl.appendChild(el('button', {
          type: 'button',
          class: 'pdp-calc__pill pdp-calc__pill--sm',
          'data-pdp-calc-len': String(l.metres)
        }, fmtLen(l.metres)));
      });
    }

    // Pick the length list for the current tab and keep each tab's chosen board.
    function useLengthsForMode() {
      var list = isProductTab() ? productLengths : genericLengths;
      if (!list.length) list = genericLengths;
      lengths = list;
      var remembered = boards[mode];
      var still = remembered != null && lengths.some(function (l) { return Math.abs(l.metres - remembered) < 0.005; });
      board = still ? remembered : defaultBoard(lengths);
      boards[mode] = board;
      renderPills();
    }

    function setLengths(list, keepBoard) {
      var clean = list.filter(function (l) { return l && l.metres > 0; });
      if (!clean.length) return;
      productLengths = clean;
      if (!keepBoard) delete boards[productMode || mode];
      useLengthsForMode();
    }

    var initial = (root.getAttribute('data-pdp-calc-lengths') || DEFAULT_LENGTHS.join(',')).split(',')
      .map(function (t) { return { label: t.trim(), metres: parseMetres(t) }; })
      .filter(function (l) { return l.metres !== null; });
    setLengths(initial.length ? initial : genericLengths, false);

    function adoptChooser() {
      var found = chooserLengths();
      if (!found || !found.options.length) return false;
      if (chooser && chooser.node === found.node) return true;
      chooser = found;
      setLengths(found.options, true);
      var current = chooserCurrent(chooser);
      if (current !== null && productMode) { boards[productMode] = current; if (isProductTab()) board = current; }
      // buy box → calculator (product tab only)
      function follow() {
        var m = chooserCurrent(chooser);
        if (m === null || !productMode) return;
        boards[productMode] = m;
        if (isProductTab() && m !== board) { board = m; render(); }
      }
      if (chooser.kind === 'dpo') {
        chooser.node.addEventListener('change', follow);
      } else if (chooser.kind === 'mrc') {
        chooser.node.addEventListener('click', function () { setTimeout(follow, 0); });
      } else {
        chooser.options.forEach(function (o) { o.input.addEventListener('change', follow); });
      }
      render();
      return true;
    }

    function applyQuantity(n) {
      var qty = findQuantityInput();
      if (!qty || !(n > 0)) return;
      qty.value = n;
      qty.dispatchEvent(new Event('input', { bubbles: true }));
      qty.dispatchEvent(new Event('change', { bubbles: true }));
      confirmText = 'Quantity set to ' + n + ' × ' + fmtLen(board) + ' in the buy box';
      render();
      var status = resultEl.querySelector('[data-pdp-calc-confirm]');
      if (status) status.focus();
    }

    function set(key, value) {
      confirmText = null;
      var v = parseFloat(value);
      if (key === 'style' || key === 'railType') {
        states[mode][key] = value;
      } else if (key === 'faces') {
        states[mode][key] = Math.round(value);
      } else {
        // Sensible ceilings: 100 m for any dimension, 50 for counts (1e9 gave "2,000,000,000 lengths")
        var ceiling = /door|rows|cols|faces|count|panels|windows/i.test(key) ? 50 : 100;
        states[mode][key] = isFinite(v) ? Math.min(ceiling, Math.max(0, v)) : 0;
      }
      render();
    }

    function activeFocusKey() {
      var a = document.activeElement;
      if (!a || !root.contains(a)) return null;
      var labelNode = a.closest('.pdp-calc__field');
      if (!labelNode) return null;
      var span = labelNode.querySelector('.pdp-calc__field-label');
      return span ? span.textContent : null;
    }

    function render() {
      // tabs
      tabsEl.querySelectorAll('[data-pdp-calc-tab]').forEach(function (btn) {
        var selected = btn.getAttribute('data-pdp-calc-tab') === mode;
        btn.classList.toggle('is-active', selected);
        // Real tabs: keep the selected state and the panel's label in step, and
        // keep only the selected tab in the tab order, as a tablist should.
        btn.setAttribute('aria-selected', selected ? 'true' : 'false');
        btn.tabIndex = selected ? 0 : -1;
        if (selected) {
          var panel = document.getElementById('pdp-calc-panel');
          if (panel && btn.id) panel.setAttribute('aria-labelledby', btn.id);
        }
      });
      // board pills
      boardEl.querySelectorAll('[data-pdp-calc-len]').forEach(function (btn) {
        btn.classList.toggle('is-active', parseFloat(btn.getAttribute('data-pdp-calc-len')) === board);
      });

      var modeDef = MODES.find(function (m) { return m.id === mode; }).mod;
      var s = states[mode];
      var calc = modeDef.compute(s, board);

      var focusKey = activeFocusKey();
      var selStart = null, selEnd = null;
      var activeBefore = document.activeElement;
      if (activeBefore && activeBefore.tagName === 'INPUT') {
        try { selStart = activeBefore.selectionStart; selEnd = activeBefore.selectionEnd; } catch (e) {}
      }

      bodyEl.innerHTML = '';
      var bodyNodes = modeDef.body(s, set);
      bodyNodes.forEach(function (n) { if (n) bodyEl.appendChild(n); });

      resultEl.innerHTML = '';
      var resNodes = modeDef.result(s, board, calc);
      resNodes.forEach(function (n) { if (n) resultEl.appendChild(n); });

      // Hand the total to the buy box: "You need N lengths" → quantity input. Product tab
      // only; the other tabs are estimates for a different product, so link to its collection.
      var need = calc.lengths;
      if (!isProductTab()) {
        var name = LABELS[mode] || mode;
        resultEl.appendChild(el('p', { className: 'pdp-calc__foot pdp-calc__estimate' },
          'Estimate on typical ' + name + ' lengths (' + lengths.map(function (l) { return fmtLen(l.metres); }).join(' / ') + ').',
          productMode ? ' This page is ' + (root.getAttribute('data-pdp-calc-product-title') || 'a different product') + '; open one of our ' + name + ' products to use its exact lengths and set the quantity.' : ' Open a product to use its exact lengths and set the quantity.'
        ));
        if (links[mode]) {
          resultEl.appendChild(el('a', { className: 'pdp-calc__apply pdp-calc__apply--link', href: links[mode] }, 'Shop ' + name + ' →'));
        }
      } else if (need > 0 && findQuantityInput()) {
        resultEl.appendChild(el('button', {
          type: 'button',
          class: 'pdp-calc__apply',
          'data-pdp-calc-apply': '',
          onClick: function () { applyQuantity(need); }
        }, 'Set quantity to ' + need + (need === 1 ? ' length' : ' lengths')));
        if (confirmText) {
          resultEl.appendChild(el('p', { className: 'pdp-calc__confirm', role: 'status', tabindex: '-1', 'data-pdp-calc-confirm': '' },
            confirmText + ' · ',
            el('a', { href: '#', onClick: function (e) {
              e.preventDefault();
              var qty = findQuantityInput();
              var target = (qty && qty.closest('form')) || qty;
              if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } }, 'check your options and add to cart')
          ));
        }
      }

      // restore focus
      if (focusKey) {
        var labels = bodyEl.querySelectorAll('.pdp-calc__field-label');
        for (var i = 0; i < labels.length; i++) {
          if (labels[i].textContent === focusKey) {
            var inp = labels[i].parentNode.querySelector('input');
            if (inp) {
              inp.focus();
              if (selStart != null) {
                try { inp.setSelectionRange(selStart, selEnd); } catch (e) {}
              }
            }
            break;
          }
        }
      }
    }

    tabsEl.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-pdp-calc-tab]');
      if (!btn) return;
      mode = btn.getAttribute('data-pdp-calc-tab');
      confirmText = null;
      useLengthsForMode();
      render();
    });

    // Only the selected tab is in the tab order, so the others have to be
    // reachable with the arrow keys, as a tablist requires.
    tabsEl.addEventListener('keydown', function (e) {
      var keys = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: 1, ArrowUp: -1 };
      var isEnd = e.key === 'End';
      var isHome = e.key === 'Home';
      if (!(e.key in keys) && !isEnd && !isHome) return;
      var tabs = Array.prototype.slice.call(tabsEl.querySelectorAll('[data-pdp-calc-tab]'));
      if (!tabs.length) return;
      var current = tabs.indexOf(document.activeElement);
      if (current === -1) return;
      e.preventDefault();
      var next;
      if (isHome) next = 0;
      else if (isEnd) next = tabs.length - 1;
      else next = (current + keys[e.key] + tabs.length) % tabs.length;
      mode = tabs[next].getAttribute('data-pdp-calc-tab');
      confirmText = null;
      useLengthsForMode();
      render();
      tabs[next].focus();
    });

    boardEl.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-pdp-calc-len]');
      if (!btn) return;
      board = parseFloat(btn.getAttribute('data-pdp-calc-len'));
      boards[mode] = board;
      confirmText = null;
      render();
      // calculator → buy box (product tab only)
      if (isProductTab()) chooserSelect(chooser || chooserLengths(), board);
    });

    // Watch for the buy box's Length row (DPO renders it asynchronously; the theme editor
    // can also re-render sections). Cheap: one query per poll, stops once adopted.
    if (!adoptChooser()) {
      var tries = 0;
      var poll = setInterval(function () {
        if (adoptChooser() || ++tries > 80) clearInterval(poll);
      }, 250);
    }
    if (window.jQuery) {
      try { window.jQuery(window).on('dpo_loading_finished', function () { setTimeout(adoptChooser, 100); }); } catch (e) {}
    }

    // On mobile, relocate the length selector to sit directly above the
    // result card, so users can flick between lengths and watch "You need"
    // update without scrolling back up to the toolbar. Restored to the
    // toolbar on desktop. The same node is moved, so its click listener and
    // active-state sync in render() keep working regardless of position.
    var boardHomeParent = boardEl.parentNode;
    function placeBoard(isMobile) {
      if (isMobile) {
        if (!(boardEl.parentNode === resultEl.parentNode && boardEl.nextSibling === resultEl)) {
          resultEl.parentNode.insertBefore(boardEl, resultEl);
        }
        boardEl.classList.add('pdp-calc__board--mobile');
      } else {
        boardEl.classList.remove('pdp-calc__board--mobile');
        if (boardEl.parentNode !== boardHomeParent) {
          boardHomeParent.appendChild(boardEl);
        }
      }
    }
    var boardMq = window.matchMedia('(max-width: 989px)');
    placeBoard(boardMq.matches);
    if (boardMq.addEventListener) {
      boardMq.addEventListener('change', function (e) { placeBoard(e.matches); });
    } else if (boardMq.addListener) {
      boardMq.addListener(function (e) { placeBoard(e.matches); });
    }

    render();
  }

  function initAll() {
    document.querySelectorAll('[data-pdp-calc]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  // Also expose for any sections loaded after DOMContentLoaded (e.g. theme editor reloads)
  document.addEventListener('shopify:section:load', initAll);
})();
